import React, { useState, useId, forwardRef } from 'react';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Tooltip, CircularProgress } from '@mui/material';
import { PDFDocument } from 'pdf-lib';
import DocumentViewer from '../DocumentViewer';

import './fileInput.scss';

// Función para comprimir imagen
const compressImage = (file, quality = 0.2) => {
    return new Promise((resolve, reject) => {
        const fileExtension = file.name.split('.').pop().toUpperCase();

        if (!['JPG', 'JPEG', 'JFIF', 'PNG', 'HEIC'].includes(fileExtension)) {
            resolve(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Error al comprimir imagen'));
                            return;
                        }
                        const compressedFile = new File(
                            [blob],
                            file.name.replace(/\.[^/.]+$/, '') + '.jpg',
                            { type: 'image/jpeg' }
                        );
                        resolve(compressedFile);
                    },
                    'image/jpeg',
                    quality
                );
            };
            img.onerror = () => reject(new Error('Error al cargar imagen'));
            img.src = event.target.result;
        };
        reader.onerror = () => reject(new Error('Error al leer archivo'));
        reader.readAsDataURL(file);
    });
};

const compressImages = async (files, quality = 0.2) => {
    const compressedFiles = [];
    for (const file of files) {
        const compressed = await compressImage(file, quality);
        compressedFiles.push(compressed);
    }
    return compressedFiles;
};

const imagesToPdf = async (files) => {
    if (!files || files.length === 0) {
        throw new Error('No hay archivos para procesar');
    }

    const pdfFiles = files.filter(f => 
        f.name.split('.').pop().toUpperCase() === 'PDF'
    );

    const imageFiles = files.filter(f => 
        ['JPG','JPEG','JFIF','PNG'].includes(
            f.name.split('.').pop().toUpperCase()
        )
    );

    // 🔹 Si solo es un PDF → devolverlo intacto
    if (pdfFiles.length === 1 && imageFiles.length === 0) {
        return pdfFiles[0];
    }

    const pdfDoc = await PDFDocument.create();

    // 🔹 Agregar PDFs existentes
    for (const file of pdfFiles) {
        const arrayBuffer = await file.arrayBuffer();
        const existingPdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
        const pages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
        pages.forEach(page => pdfDoc.addPage(page));
    }

    // 🔹 Agregar imágenes
    for (const file of imageFiles) {
        const arrayBuffer = await file.arrayBuffer();
        let image;

        const ext = file.name.split('.').pop().toUpperCase();

        if (['JPG','JPEG','JFIF'].includes(ext)) {
            image = await pdfDoc.embedJpg(arrayBuffer);
        } else if (ext === 'PNG') {
            image = await pdfDoc.embedPng(arrayBuffer);
        }

        const page = pdfDoc.addPage([image.width, image.height]);
        page.drawImage(image, {
            x: 0,
            y: 0,
            width: image.width,
            height: image.height,
        });
    }

    const pdfBytes = await pdfDoc.save();
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
    const lastFile = files[files.length - 1];
    const lastFileName = lastFile?.name || 'documento';
    const pdfFileName = lastFileName.replace(/\.[^/.]+$/, '') + '.pdf';

    return new File([pdfBlob], pdfFileName, { type: 'application/pdf' });
};
/*

const imagesToPdf = async (files) => {
    if (!files || files.length === 0) {
        throw new Error('No hay archivos para procesar');
    }

    const pdfDoc = await PDFDocument.create();

    for (const file of files) {
        if (!file || !file.name) continue;

        const arrayBuffer = await file.arrayBuffer();
        const fileExtension = file.name.split('.').pop().toUpperCase();

        let image;
        if (['JPG', 'JPEG', 'JFIF'].includes(fileExtension)) {
            image = await pdfDoc.embedJpg(arrayBuffer);
        } else if (fileExtension === 'PNG') {
            image = await pdfDoc.embedPng(arrayBuffer);
        } else if (fileExtension === 'PDF') {
            const existingPdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
            const pages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
            pages.forEach(page => pdfDoc.addPage(page));
            continue;
        } else {
            continue;
        }

        const page = pdfDoc.addPage([image.width, image.height]);
        page.drawImage(image, {
            x: 0,
            y: 0,
            width: image.width,
            height: image.height,
        });
    }

    const pdfBytes = await pdfDoc.save();
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
    const lastFile = files[files.length - 1];
    const lastFileName = lastFile?.name || 'documento';
    const pdfFileName = lastFileName.replace(/\.[^/.]+$/, '') + '.pdf';

    return new File([pdfBlob], pdfFileName, { type: 'application/pdf' });
};
*/


const fileToViewerData = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result.split(',')[1];

            resolve({
                titulo: file.name,
                archivo: base64,
                extension: file.name.split('.').pop().toUpperCase(),
                contentType: file.type
            });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

const mergeServerDocuments = async (documents) => {
    const pdfDoc = await PDFDocument.create();

    for (const doc of documents) {
        const base64Content = doc.archivo;
        if (!base64Content) continue;

        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        const extension = doc.extension?.replace('.', '').toUpperCase() || '';

        if (['JPG', 'JPEG', 'JFIF'].includes(extension)) {
            const image = await pdfDoc.embedJpg(bytes);
            const page = pdfDoc.addPage([image.width, image.height]);
            page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
        } else if (extension === 'PNG') {
            const image = await pdfDoc.embedPng(bytes);
            const page = pdfDoc.addPage([image.width, image.height]);
            page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
        } else if (extension === 'PDF') {
            const existingPdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
            const pages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
            pages.forEach(page => pdfDoc.addPage(page));
        }
    }

    const pdfBytes = await pdfDoc.save();

    // Convertir a base64 en chunks para evitar stack overflow
    const base64Pdf = uint8ArrayToBase64(pdfBytes);

    return {
        titulo: 'Documentos combinados',
        archivo: base64Pdf,
        extension: '.pdf',
        contentType: 'application/pdf'
    };
};

// Función helper para convertir Uint8Array a base64 sin stack overflow
const uint8ArrayToBase64 = (bytes) => {
    const chunkSize = 8192;
    let binary = '';

    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
    }

    return btoa(binary);
};
// Función para obtener el nombre a mostrar
const getDisplayFileName = (loadedFileName, loadedFileData, maxLength = 40) => {
    // Si es un array de documentos del servidor
    if (Array.isArray(loadedFileData) && loadedFileData.length > 0) {
        const titles = loadedFileData.map(doc => doc.titulo || 'Sin título');
        const combined = titles.join(', ');

        if (combined.length > maxLength) {
            return combined.substring(0, maxLength - 3) + '...';
        }
        return combined;
    }

    // Si es un solo documento o string
    return loadedFileName;
};

export const FileInput = forwardRef(({
    showUploadIcon = true,
    label = '',
    validExtensions = ['PDF', 'PNG', 'JPG', 'HEIC', 'JPEG', 'JFIF'],
    maxFileSize = 2,
    onChange,
    mobileLayout,
    loadedFileName,
    loadedFileData = null,
    onDeleteImage = null,
    disabled,
    onClickImagen = null,
    multiple = true,
    combineAsPdf = true,
    maxFiles = 10,
    enablePreview = true,
    compressImages: shouldCompress = true,
    imageQuality = 0.2,
    maxDisplayLength = 40,
    disabledLabel,
    ...props
}, ref) => {
    const [error, setError] = useState('');
    const [changeFile, setChangeFile] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [openPreview, setOpenPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [currentFile, setCurrentFile] = useState(null);
    const inputId = useId();
    const showDisabledMessage = disabled && !!disabledLabel;
    const displayFileName = getDisplayFileName(loadedFileName, loadedFileData, maxDisplayLength);

    const validateFile = (file) => {
        const { name, size } = file;
        const fileExtension = name.split('.').pop();
        const fileSizeToMB = (size / (1024 * 1024))?.toFixed(2);
        const fileNameIsValid = !(new RegExp(/[&\#,$~^%|\/":?<>{}]/g).test(name));

        if (!shouldCompress && fileSizeToMB > maxFileSize) {
            return `Limite de tamaño alcanzado. (Max ${maxFileSize}MB)`;
        }
        if (!validExtensions?.includes(fileExtension?.toUpperCase())) {
            return 'Extensión de archivo invalida.';
        }
        if (!fileNameIsValid) {
            return "Nombre del archivo no valido";
        }
        return null;
    };

    const handleFileChange = async (files) => {
        if (!files || files.length === 0) return;

        const fileList = Array.from(files).filter(f => f && f.name);
        if (fileList.length === 0) return;

        for (const file of fileList) {
            const errorMsg = validateFile(file);
            if (errorMsg) {
                setError(errorMsg);
                return;
            }
        }

        if (multiple && combineAsPdf && fileList.length > 1) {  
            if (fileList.length > maxFiles) {
                setError(`Máximo ${maxFiles} archivos permitidos.`);
                return;
            }

            setIsProcessing(true);
            setError('');

            try {
                const filesToProcess = shouldCompress
                    ? await compressImages(fileList, imageQuality)
                    : fileList;

                const pdfFile = await imagesToPdf(filesToProcess);

                const pdfSizeMB = (pdfFile.size / (1024 * 1024)).toFixed(2);
                if (pdfSizeMB > maxFileSize) {
                    setError(`El PDF resultante excede el límite (${pdfSizeMB}MB > ${maxFileSize}MB)`);
                    setIsProcessing(false);
                    return;
                }

                setCurrentFile(pdfFile);
                setChangeFile(false);
                onChange(pdfFile);
            } catch (err) {
                console.error('Error al crear PDF:', err);
                setError('Error al procesar los archivos');
            } finally {
                setIsProcessing(false);
            }
        } else if (multiple) {
            if (fileList.length === 1) {
                const file = shouldCompress
                    ? await compressImage(fileList[0], imageQuality)
                    : fileList[0];

                setCurrentFile(file);
                setChangeFile(false);
                onChange(file); // 👈 DEVUELVE UN SOLO FILE
                return;
            }
            if (fileList.length > maxFiles) {
                setError(`Máximo ${maxFiles} archivos permitidos.`);
                return;
            }

            setIsProcessing(true);
            setError('');

            try {
                const filesToReturn = shouldCompress
                    ? await compressImages(fileList, imageQuality)
                    : fileList;

                setError('');
                setChangeFile(false);
                onChange(filesToReturn);
            } catch (err) {
                console.error('Error al comprimir:', err);
                setError('Error al procesar los archivos');
            } finally {
                setIsProcessing(false);
            }
        } else {
            setIsProcessing(true);
            setError('');

            try {
                const file = fileList[0];
                const fileToReturn = shouldCompress
                    ? await compressImage(file, imageQuality)
                    : file;

                const fileSizeMB = (fileToReturn.size / (1024 * 1024)).toFixed(2);
                if (fileSizeMB > maxFileSize) {
                    setError(`El archivo excede el límite (${fileSizeMB}MB > ${maxFileSize}MB)`);
                    setIsProcessing(false);
                    return;
                }

                setCurrentFile(fileToReturn);
                setChangeFile(false);
                onChange(fileToReturn);
            } catch (err) {
                console.error('Error al comprimir:', err);
                setError('Error al procesar el archivo');
            } finally {
                setIsProcessing(false);
            }
        }
    };

    const handlePreviewClick = async () => {
        if (onClickImagen) {
            onClickImagen();
            return;
        }

        if (!enablePreview) return;

        // Múltiples documentos del servidor - mergear al vuelo
        if (Array.isArray(loadedFileData) && loadedFileData.length > 1) {
            setIsProcessing(true);
            try {
                const mergedPdf = await mergeServerDocuments(loadedFileData);
                setPreviewData(mergedPdf);
                setOpenPreview(true);
            } catch (err) {
                console.error('Error al mergear documentos:', err);
                setError('Error al preparar vista previa');
            } finally {
                setIsProcessing(false);
            }
            return;
        }

        // Un solo documento del servidor
        if (Array.isArray(loadedFileData) && loadedFileData.length === 1) {
            setPreviewData(loadedFileData[0]);
            setOpenPreview(true);
            return;
        }

        // Documento único (objeto)
        if (loadedFileData && !Array.isArray(loadedFileData)) {
            setPreviewData(loadedFileData);
            setOpenPreview(true);
            return;
        }

        // Archivo local
        if (currentFile) {
            try {
                const viewerData = await fileToViewerData(currentFile);
                setPreviewData(viewerData);
                setOpenPreview(true);
            } catch (err) {
                console.error('Error al preparar preview:', err);
            }
        }
    };

    const handleDelete = () => {
        setCurrentFile(null);
        setPreviewData(null);
        if (onDeleteImage) {
            onDeleteImage();
        } else {
            setError('');
            setChangeFile(true);
        }
    };

    const hasLoadedFile = loadedFileName || (Array.isArray(loadedFileData) && loadedFileData.length > 0);
    return (
        <>
            <label
                aria-hidden='true'
                htmlFor={inputId}
                ref={ref}
                className='label-wrapper'
                onClick={(e) => e.target.value = ''}
                onChange={({ target: { files } }) => handleFileChange(files)}
            >
                <div
                    aria-hidden='true'
                    onClick={(e) => {
                        if (!isProcessing && !error && hasLoadedFile) {
                            e.preventDefault();
                            handlePreviewClick();
                        }
                    }}
                    className={`file-input-container 
                        ${!mobileLayout && 'desktop-settings'}
                        ${hasLoadedFile ? 'loaded-file' : 'no-file'}
                    `}
                    {...props}
                >
                    {isProcessing && (
                        <div className='processing-container'>
                            <CircularProgress size={20} />
                            <span style={{ marginLeft: '10px' }}>Procesando archivos...</span>
                        </div>
                    )}

                    {!isProcessing && error && (
                        <div className='loaded-file-container'>
                            <span className='file-name-label error-label'>
                                {error}
                            </span>
                            <Tooltip title='Cargar otro archivo'>
                                <UploadFileIcon
                                    onClick={() => {
                                        setError('');
                                        if (onDeleteImage) onDeleteImage();
                                    }}
                                    className='upload-icon'
                                />
                            </Tooltip>
                        </div>
                    )}

                    {!isProcessing && !error && hasLoadedFile && !changeFile && (
                        <div className='loaded-file-container'>
                            <span
                                className={`file-name-label ${disabled && 'disabled-label'}`}
                                style={{ cursor: enablePreview || onClickImagen ? 'pointer' : 'default' }}
                            >
                                {displayFileName}
                            </span>
                            {!disabled && (
                                <Tooltip title='Eliminar archivo'>
                                    <DeleteOutlineOutlinedIcon
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleDelete();
                                        }}
                                        className='delete-icon'
                                    />
                                </Tooltip>
                            )}
                        </div>
                    )}

                    {!isProcessing && !error && (!hasLoadedFile || changeFile) && (
                        <div className={`file-input-content ${disabled && 'disabled-label'} ${mobileLayout && 'mobile-orientation'}`}>
                            <input
                                type='file'
                                hidden
                                id={inputId}
                                disabled={disabled}
                                multiple={multiple}
                            />
                            {showUploadIcon && <UploadFileIcon className='upload-icon' />}
                            <span className={`upload-label ${disabled && 'disabled-label'}`}>{label}</span>
                            <div className='upload-extensions'>
                                {validExtensions?.map((e, index) => {
                                    const isLast = !validExtensions[index + 1];
                                    return (
                                        <span key={e} className={`upload-settings label-margin ${disabled && 'disabled-label'}`}>
                                            {e}{!isLast && ','}
                                        </span>
                                    )
                                })}
                                <span className={`upload-settings ${disabled && 'disabled-label'}`}>(max. {maxFileSize}MB)</span>
                            </div>
                            {showDisabledMessage && (
                                <div className='loaded-file-container'>
                                    <span className='file-name-label error-label'>
                                        {disabledLabel}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </label>
            {openPreview && previewData && (
                <DocumentViewer
                    data={previewData}
                    setOpenDlg={setOpenPreview}
                    download={true}
                />
            )}
        </>
    );
});